// A new notification (a rematch, a duel invite, a price alert, ...) pops up on whatever page the user is on, in the
// same card design as the bell dropdown (NotificationBell.jsx). Clicking it marks it read and opens the page it's
// about (notificationRoutes.js); otherwise it goes away by itself. Reads the app's one notifications poll
// (notificationsFeed.js, shared with the bell).
// The first load of a browser only sets the baseline: older notifications never pop up, only ones that arrive later.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import { bellPalette, severityColor } from './NotificationBell';
import { NOTIFICATIONS_CHANGED, routeForNotification } from './notificationRoutes';
import { notificationsFeed } from './notificationsFeed';
import { usePoller } from '../hooks/sharedPoll';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
const SHOW_MS = 10000;
const MAX_SHOWN = 3;
const FRESH_MS = 24 * 3600 * 1000; // never pop up anything older than a day
const SEEN_KEY = 'nm.notifPopups.lastId';
// Arena pages are always dark; elsewhere the pop-up follows the app's light/dark setting.
const DARK_PATHS = /^\/(duel|duels|challenge|arena)(\/|$)/;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const readSeen = () => { try { return Number(localStorage.getItem(SEEN_KEY)) || null; } catch { return null; } };
const writeSeen = (id) => { try { localStorage.setItem(SEEN_KEY, String(id)); } catch { /* storage unavailable */ } };
const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function isDark(pathname) {
  if (DARK_PATHS.test(pathname)) return true;
  let mode = null;
  try { mode = localStorage.getItem('mui-mode'); } catch { /* storage unavailable */ }
  if (mode === 'light' || mode === 'dark') return mode === 'dark';
  return !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
}

function Popup({ n, pal, onOpen, onClose }) {
  const [hover, setHover] = useState(false);
  useEffect(() => {
    if (hover) return undefined;
    const t = setTimeout(() => onClose(n.notification_id), SHOW_MS);
    return () => clearTimeout(t);
  }, [hover, onClose, n.notification_id]);
  const sev = severityColor(n.severity);
  return (
    <Box role="alert" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      sx={{
        position: 'relative', width: '100%', bgcolor: pal.unreadBg, color: pal.titleText,
        border: `1px solid ${pal.border}`, borderRadius: '12px', overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        animation: reducedMotion() ? 'none' : 'nmPopIn 200ms ease-out',
      }}>
      <Box component="button" type="button" onClick={() => onOpen(n)}
        sx={{
          all: 'unset', boxSizing: 'border-box', display: 'block', width: '100%', px: 1.5, py: 1, pr: 4, cursor: 'pointer',
          '&:hover': { bgcolor: pal.unreadHover }, '&:focus-visible': { outline: '2px solid #0A84FF', outlineOffset: -2 },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: pal.titleText, flex: 1, minWidth: 0 }}>{n.title}</Typography>
          <Chip size="small" label={n.severity} sx={{ height: 16, fontSize: '0.58rem', color: sev, bgcolor: `${sev}18` }} />
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: pal.bodyText, mt: 0.25 }}>{n.message}</Typography>
        <Typography sx={{ fontSize: '0.62rem', color: pal.timeText, mt: 0.25 }}>
          {routeForNotification(n) ? 'Tap to open' : 'Tap to see it in Notifications'}
        </Typography>
      </Box>
      <Box component="button" type="button" aria-label="Dismiss" onClick={() => onClose(n.notification_id)}
        sx={{
          all: 'unset', position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%',
          display: 'grid', placeItems: 'center', cursor: 'pointer', color: pal.bodyText, fontSize: 16, lineHeight: 1,
          '&:hover': { bgcolor: pal.unreadHover }, '&:focus-visible': { outline: '2px solid #0A84FF' },
        }}>×</Box>
    </Box>
  );
}

export default function NotificationPopups() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [shown, setShown] = useState([]);
  const seenRef = useRef(readSeen());

  const list = usePoller(notificationsFeed);

  useEffect(() => {
    if (!Array.isArray(list) || !list.length) return;
    const maxId = Math.max(...list.map((n) => n.notification_id));
    const seen = seenRef.current;
    seenRef.current = Math.max(seen || 0, maxId);
    writeSeen(seenRef.current);
    if (seen == null) return; // first load in this browser: baseline only
    const now = Date.now();
    const fresh = list.filter((n) => n.notification_id > seen && !n.is_read
      && now - new Date(n.created_at).getTime() < FRESH_MS);
    if (fresh.length) {
      setShown((cur) => [...fresh.sort((x, y) => x.notification_id - y.notification_id), ...cur]
        .filter((n, i, all) => all.findIndex((m) => m.notification_id === n.notification_id) === i)
        .slice(0, MAX_SHOWN));
    }
  }, [list]);

  const close = useCallback((id) => setShown((cur) => cur.filter((n) => n.notification_id !== id)), []);

  const open = (n) => {
    close(n.notification_id);
    fetch(`${API_BASE}/api/notifications/${n.notification_id}/read`, { method: 'PUT', headers: authHeaders() })
      .then(() => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED)))
      .catch(() => {});
    navigate(routeForNotification(n) || '/app/notifications');
  };

  if (!shown.length) return null;
  const pal = bellPalette(isDark(pathname));
  return (
    <Box aria-live="polite" sx={{
      position: 'fixed', zIndex: 1400, top: 'calc(env(safe-area-inset-top, 0px) + 72px)', right: 16,
      width: 'min(360px, calc(100vw - 32px))', display: 'flex', flexDirection: 'column', gap: 1,
    }}>
      <style>{'@keyframes nmPopIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }'}</style>
      {shown.map((n) => (
        <Popup key={n.notification_id} n={n} pal={pal} onOpen={open} onClose={close} />
      ))}
    </Box>
  );
}
